import * as React from 'react'
import { FunctionComponent, ReactElement } from 'react'
import { ActionButton, Dialog, DialogFooter, DialogType, PrimaryButton, Stack, Text } from '@fluentui/react'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../../reducers'
import { dismissError, ErrorAction, ErrorType, NormalizedError } from '../../reducers/errorReducer'
import { asBoolean } from '../../helpers/functionHelper'
import { Button, MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components'
import { DismissRegular } from '@fluentui/react-icons'

type ErrorMessageProps = {
    error: NormalizedError
    dismiss?: () => void
}

export const NetworkError: FunctionComponent<ErrorMessageProps> = (props: ErrorMessageProps) => {
    return (
        <Dialog
            hidden={false}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: props.error.message,
                subText: 'Er kon geen verbinding worden gemaakt met HAMMOCK. Controleer uw internetverbinding of ' + 'probeer het later nog eens.',
            }}
        >
            <DialogFooter>
                <ActionButton onClick={props.dismiss} text="Sluiten" />
                <PrimaryButton
                    onClick={() => {
                        props.dismiss()
                        window.location.reload()
                    }}
                    text="Pagina vernieuwen"
                />
            </DialogFooter>
        </Dialog>
    )
}

export const FrontendError: FunctionComponent<ErrorMessageProps> = ({ dismiss }: ErrorMessageProps) => {
    return (
        <Dialog
            hidden={false}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: 'Er is een onbekende fout opgetreden',
            }}
            minWidth={240}
            maxWidth={720}
        >
            <Stack tokens={{ childrenGap: 's1' }}>
                <Text block>
                    Neem voor hulp contact op met support via <strong>026&nbsp;-&nbsp;206&nbsp;21&nbsp;99</strong> of{' '}
                    <strong>support@hammock.nl</strong>.
                </Text>
            </Stack>
            <DialogFooter>{dismiss && <PrimaryButton onClick={dismiss} text="Melding sluiten" />}</DialogFooter>
        </Dialog>
    )
}

export const DefaultError: FunctionComponent<ErrorMessageProps> = ({ error, dismiss }) => (
    <MessageBar intent={'error'} layout={'multiline'}>
        <MessageBarBody>
            <MessageBarTitle> {error.message} </MessageBarTitle>
            {asBoolean(process.env.VERBOSE_ERRORS) && error.trace && <pre>{error.trace}</pre>}
            {asBoolean(process.env.VERBOSE_ERRORS) && error.exception && <pre>{error.exception.stack}</pre>}
        </MessageBarBody>
        <MessageBarActions
            containerAction={<Button onClick={dismiss} aria-label="dismiss" appearance="transparent" icon={<DismissRegular />} />}
        ></MessageBarActions>
    </MessageBar>
)

export default function Errors(): ReactElement {
    const errorState = useSelector<Store, Map<string, NormalizedError>>(state => state.errors)
    const errors = [...errorState?.values()]
    const dispatch = useDispatch()
    const dismiss = (error: NormalizedError) => dispatch(dismissError(error.id))
    const errorMap = errors?.map((error: NormalizedError, i: number) => {
        switch (error.type) {
            case ErrorType.Network:
                return <NetworkError error={error} dismiss={() => dispatch({ type: ErrorAction.DismissNetworkError })} key={i} />
            case ErrorType.Frontend:
                return <FrontendError error={error} dismiss={() => dismiss(error)} key={i} />
            default:
                return <DefaultError error={error} dismiss={() => dismiss(error)} key={i} />
        }
    })

    return <>{errorMap}</>
}
